// BruchtestDisplayPage.jsx — Vollbild-Anzeige für zweiten Bildschirm / Beamer
import React, { useState, useEffect } from 'react';
import './BruchtestDisplayPage.css';

const CHANNEL = 'tda-bruchtest';
const LS_KEY  = 'tda-bruchtest-state';

export default function BruchtestDisplayPage() {
  const [state, setState]             = useState(null);
  const [animKey, setAnimKey]         = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Overflow verstecken
  useEffect(() => {
    const prev = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.html;
      document.body.style.overflow = prev.body;
    };
  }, []);

  // Auto-Vollbild beim Öffnen
  useEffect(() => {
    const t = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
  };

  // Externe Elemente (Chat-Widget etc.) ausblenden
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT','STYLE','LINK','META'].includes(el.tagName))
          el.style.setProperty('display', 'none', 'important');
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  // BroadcastChannel + localStorage lauschen
  useEffect(() => {
    const apply = (raw) => {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        setState(parsed || null);
        setAnimKey(k => k + 1);
      } catch {}
    };

    apply(localStorage.getItem(LS_KEY));

    const onStorage = (e) => { if (e.key === LS_KEY) apply(e.newValue); };
    window.addEventListener('storage', onStorage);

    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => apply(e.data);
    } catch {}

    return () => {
      window.removeEventListener('storage', onStorage);
      ch?.close();
    };
  }, []);

  if (!isFullscreen) {
    return <SetupOverlay onFullscreen={toggleFullscreen} />;
  }

  const isScoring = state?.type === 'scoring' && state?.currentTeilnehmer;
  const isWinner  = state?.type === 'winner'  && state?.ranking?.length > 0;
  return isWinner
    ? <WinnerScreen key={animKey} data={state} onFullscreen={toggleFullscreen} />
    : isScoring
      ? <ScoringScreen key={animKey} data={state} onFullscreen={toggleFullscreen} />
      : <StandbyScreen data={state} onFullscreen={toggleFullscreen} />;
}

// ── Setup-Overlay ─────────────────────────────────────────────────────────
function SetupOverlay({ onFullscreen }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/bruchtest-display`;
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };
  return (
    <div className="btd-setup">
      <div className="btd-setup-icon">🧱</div>
      <div className="btd-setup-card">
        <div className="btd-setup-title">Bruchtest-Anzeige einrichten</div>
        <div className="btd-setup-option">
          <div className="btd-setup-option-label">Option A — Fenster verschieben</div>
          <p className="btd-setup-hint">Fenster an den Bildschirmrand ziehen → auf Monitor 2 → Vollbild starten</p>
          <button className="btd-setup-btn" onClick={onFullscreen}>⛶ Vollbild starten</button>
        </div>
        <div className="btd-setup-divider">oder</div>
        <div className="btd-setup-option">
          <div className="btd-setup-option-label">Option B — URL direkt öffnen</div>
          <div className="btd-setup-url">{url}</div>
          <button className="btd-setup-copy" onClick={copy}>{copied ? '✓ Kopiert!' : '📋 URL kopieren'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Standby-Screen ────────────────────────────────────────────────────────
function StandbyScreen({ data, onFullscreen }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="btd-standby">
      <button className="btd-fs-btn" onClick={onFullscreen} title="Vollbild">⛶</button>
      <div className="btd-standby-kanji">破</div>
      <div className="btd-standby-title">Bruchtest</div>
      {data?.kategorie && <div className="btd-standby-kat">{data.kategorie}</div>}
      <div className="btd-standby-hint">Bereit für Bewertung</div>
      <div className="btd-standby-time">
        {time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
}

// ── Winner-Screen ─────────────────────────────────────────────────────────
function WinnerScreen({ data, onFullscreen }) {
  const { ranking = [], kategorie } = data;
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);

  const top3 = ranking.slice(0, 3);
  const rest = ranking.slice(3);
  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div className={`btd-winner${visible ? ' btd-winner--visible' : ''}`}>
      <button className="btd-fs-btn" onClick={onFullscreen} title="Vollbild">⛶</button>
      <div className="btd-winner-kanji">勝</div>
      <div className="btd-winner-title">Ergebnisse</div>
      {kategorie && <div className="btd-winner-kat">{kategorie}</div>}

      <div className="btd-winner-podium">
        {/* Reihenfolge: 2 – 1 – 3 (klassisches Podium) */}
        {[1, 0, 2].map(i => {
          const r = top3[i];
          if (!r) return null;
          return (
            <div key={r.anmeldung_id} className={`btd-winner-place btd-winner-place--${i + 1}`}>
              <div className="btd-winner-medal">{medals[i]}</div>
              <div className="btd-winner-name">
                {r.vorname} <strong>{r.nachname}</strong>
              </div>
              {r.verein_name && <div className="btd-winner-club">{r.verein_name}</div>}
              <div className="btd-winner-pts">{r.gesamtPunkte.toFixed(2)}</div>
            </div>
          );
        })}
      </div>

      {rest.length > 0 && (
        <div className="btd-winner-rest">
          {rest.map((r, i) => (
            <div key={r.anmeldung_id} className="btd-winner-rest-item">
              <span className="btd-winner-rest-pos">{i + 4}.</span>
              <span className="btd-winner-rest-name">{r.vorname} {r.nachname}</span>
              <span className="btd-winner-rest-club">{r.verein_name}</span>
              <span className="btd-winner-rest-pts">{r.gesamtPunkte.toFixed(1)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Scoring-Screen ────────────────────────────────────────────────────────
function ScoringScreen({ data, onFullscreen }) {
  const { currentTeilnehmer, kategorie, modus, techniken = [], gesamtpunkte = 0, ranking = [], nextTeilnehmer = null } = data;

  return (
    <div className="btd-scoring">
      <button className="btd-fs-btn" onClick={onFullscreen} title="Vollbild">⛶</button>

      {/* ── Linke Seite: Athlet + Techniken ── */}
      <div className="btd-left">
        {/* Header */}
        <div className="btd-scoring-header">
          <div className="btd-category-row">
            <span className="btd-category-name">{kategorie}</span>
            {modus && <span className="btd-modus-badge">{modus}</span>}
          </div>
        </div>

        {/* Athlete */}
        <div className="btd-athlete">
          <div className="btd-athlete-name">
            {currentTeilnehmer.vorname} <strong>{currentTeilnehmer.nachname}</strong>
          </div>
          {currentTeilnehmer.verein_name && (
            <div className="btd-athlete-club">{currentTeilnehmer.verein_name}</div>
          )}
        </div>

        {/* Techniken-Liste */}
        <div className="btd-techniken-list">
          {techniken.length === 0 ? (
            <div className="btd-no-techniken">Noch keine Techniken bewertet</div>
          ) : techniken.map((t, i) => (
            <div key={i} className={`btd-technik-item ${t.erfolg ? '' : 'fail'}`}>
              <span className="btd-technik-nr">{i + 1}</span>
              <span className="btd-technik-icon">{t.materialIcon || '🪵'}</span>
              <div className="btd-technik-info">
                <span className="btd-technik-name">
                  {t.technikName || t.technikId}
                  {t.mitSprung && <span className="btd-sprung-tag">Sprung</span>}
                </span>
                <span className="btd-technik-sub">
                  {t.anzahl}× {t.materialName}
                  {t.versuche > 1 && ` · ${t.versuche} Versuche`}
                </span>
              </div>
              <span className="btd-technik-punkte">
                {t.erfolg ? t.punkte?.toFixed(1) : '✕'}
              </span>
            </div>
          ))}
        </div>

        {/* Gesamt */}
        <div className="btd-total-row">
          <span className="btd-total-label">Gesamtpunkte</span>
          <span className="btd-total-value">{gesamtpunkte.toFixed(2)}</span>
        </div>

        {/* Nächster Teilnehmer */}
        {nextTeilnehmer && (
          <div className="btd-next-row">
            <span className="btd-next-label">Nächster</span>
            <span className="btd-next-name">
              {nextTeilnehmer.vorname} <strong>{nextTeilnehmer.nachname}</strong>
            </span>
            {nextTeilnehmer.verein_name && (
              <span className="btd-next-club">{nextTeilnehmer.verein_name}</span>
            )}
          </div>
        )}
      </div>

      {/* ── Rechte Seite: Rangliste ── */}
      <div className="btd-right">
        <div className="btd-ranking-title">Rangliste</div>
        <div className="btd-ranking-list">
          {ranking.map((r, idx) => {
            const isActive = r.anmeldung_id === currentTeilnehmer.anmeldung_id;
            const medal = ['🥇','🥈','🥉'][idx] || '';
            return (
              <div key={r.anmeldung_id} className={`btd-rank-item ${isActive ? 'active' : ''} ${idx < 3 ? `top-${idx+1}` : ''}`}>
                <span className="btd-rank-pos">
                  {medal || `${idx + 1}.`}
                </span>
                <div className="btd-rank-info">
                  <span className="btd-rank-name">{r.vorname} {r.nachname}</span>
                  <span className="btd-rank-club">{r.verein_name}</span>
                </div>
                <span className="btd-rank-pts">
                  {r.gesamtPunkte > 0 ? r.gesamtPunkte.toFixed(1) : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
